import { useId, useRef, useState, type FormEvent } from 'react'
import { History, Loader2, Search, Tag, X } from 'lucide-react'

import { useSuggestions } from '@/hooks/queries'
import { preferences, useStore } from '@/lib/store'
import { replaceLastToken, tagLabel } from '@/lib/tags'
import { cn } from '@/lib/utils'

interface SearchBoxProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (query: string) => void
  autoFocus?: boolean
  /** `dropdown` floats suggestions under the field; `inline` lists them below (mobile sheet). */
  mode?: 'dropdown' | 'inline'
  className?: string
}

/**
 * Tag search with autocomplete for the word being typed. Several tags are
 * combined with AND by the backend, so picking a suggestion completes the
 * last word instead of replacing the whole query.
 */
export function SearchBox({ value, onChange, onSubmit, autoFocus, mode = 'dropdown', className }: SearchBoxProps) {
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(-1)
  const { recentSearches } = useStore(preferences)
  const { data: suggestions = [], isFetching } = useSuggestions(value)

  const showRecent = mode === 'inline' && value.trim() === ''
  const items: { kind: 'tag' | 'recent'; value: string }[] = showRecent
    ? recentSearches.map((v) => ({ kind: 'recent', value: v }))
    : value.trim().length >= 2
      ? suggestions.map((v) => ({ kind: 'tag', value: v }))
      : []
  const open = items.length > 0 && (mode === 'inline' || focused)

  function change(next: string) {
    onChange(next)
    setActive(-1)
  }

  function pick(item: { kind: 'tag' | 'recent'; value: string }) {
    setActive(-1)
    if (item.kind === 'recent') {
      onChange(item.value)
      onSubmit(item.value)
      return
    }
    onChange(replaceLastToken(value, item.value))
    inputRef.current?.focus()
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (open && active >= 0 && items[active]) return pick(items[active])
    inputRef.current?.blur()
    onSubmit(value.trim())
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (i + 1) % items.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1))
    } else if (e.key === 'Escape' && mode === 'dropdown') {
      setFocused(false)
    }
  }

  return (
    <form role="search" onSubmit={submit} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-11 items-center gap-2 rounded-xl border bg-background/70 px-3 transition-[box-shadow,border-color]',
          'focus-within:border-primary/50 focus-within:shadow-glow',
        )}
      >
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus={autoFocus}
          placeholder="Search tags – e.g. blue_hair smile"
          value={value}
          onChange={(e) => change(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-label="Search tags"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          className="h-full min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground/70 md:text-sm"
        />
        {isFetching && value.trim().length >= 2 && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {value && (
          <button
            type="button"
            onClick={() => {
              change('')
              inputRef.current?.focus()
            }}
            className="-mr-1 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
            <span className="sr-only">Clear</span>
          </button>
        )}
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className={cn(
            mode === 'dropdown'
              ? 'absolute inset-x-0 top-full z-50 mt-2 rounded-xl border bg-popover p-1.5 shadow-soft animate-fade-in'
              : 'mt-3 space-y-0.5',
          )}
        >
          {showRecent && (
            <li className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground" role="presentation">
              Recent
            </li>
          )}
          {items.map((item, i) => {
            const Icon = item.kind === 'recent' ? History : Tag
            return (
              <li
                key={`${item.kind}-${item.value}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item)}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
                  i === active ? 'bg-accent text-accent-foreground' : 'text-foreground/90',
                )}
              >
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{item.kind === 'tag' ? tagLabel(item.value) : item.value}</span>
              </li>
            )
          })}
        </ul>
      )}
    </form>
  )
}
